import { Dialog } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { IoClose } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { getPopupPbf } from "../../../../services/purchaseApi";

// Same behavior as the Android app: at most one popup per session (a browser
// tab here, an app process there), rotating one step past the popup shown
// last so a new session never repeats it. No loading or error UI.
const SHOWN_KEY = "pbf_popup_shown";
const LAST_SHOWN_ID_KEY = "last_shown_popup_id";

const readStorage = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorage = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch {
    // Storage blocked: the popup may show again, which is harmless.
  }
};

const PopupHome = () => {
  const navigate = useNavigate();
  const [popup, setPopup] = useState(null);

  const { data } = useQuery({
    queryKey: ["popup-pbf"],
    queryFn: () => getPopupPbf(),
    enabled: !readStorage(sessionStorage, SHOWN_KEY),
    retry: false,
    refetchOnWindowFocus: false,
  });

  useEffect(() => {
    const popups = data?.data ?? [];
    if (popups.length === 0 || readStorage(sessionStorage, SHOWN_KEY)) return;

    const lastShownId = readStorage(localStorage, LAST_SHOWN_ID_KEY);
    // An id that is gone restarts the cycle: findIndex gives -1, so index 0.
    const next =
      popups[
        (popups.findIndex((item) => item.popup_uid === lastShownId) + 1) %
          popups.length
      ];
    writeStorage(localStorage, LAST_SHOWN_ID_KEY, next.popup_uid);
    writeStorage(sessionStorage, SHOWN_KEY, "1");
    setPopup(next);
  }, [data]);

  const handleClose = () => setPopup(null);

  // Only a popup linked to a banner is clickable.
  const handleClick = () => {
    if (!popup?.banner_uid) return;
    handleClose();
    navigate(`/purchase-pbf/banner/${popup.banner_uid}`);
  };

  return (
    <Dialog
      open={!!popup}
      onClose={handleClose}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          background: "transparent",
          boxShadow: "none",
          width: "100%",
          maxWidth: "360px",
          margin: "32px",
        },
      }}
    >
      {popup ? (
        <div className="relative">
          <img
            src={popup.image}
            alt={popup.popup_name}
            onClick={handleClick}
            className={`w-full aspect-[2/3] object-cover rounded-md ${popup.banner_uid ? "cursor-pointer" : ""}`}
          />
          <button
            type="button"
            aria-label="Tutup"
            onClick={handleClose}
            className="absolute top-1 right-1 rounded-full bg-black/20 text-white p-1 hover:bg-black/40"
          >
            <IoClose className="text-[28px]" />
          </button>
        </div>
      ) : null}
    </Dialog>
  );
};

export default PopupHome;
